import classNames from "classnames";
import { BillingSlider } from "components/BillingSlider/BillingSlider";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Slider from "components/DesignSystem/Slider/Slider";
import { CartPaymentIcon } from "components/icons/CartPaymentIcon";
import { ChatCartPaid } from "components/icons/Chat/ChatCartPaid";
import { Cross } from "components/icons/Cross";
import { MessageComponentProps } from "components/MessageBubble/MessageBubble";
import { OPEN_CHECKOUT } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetGroupDataQuery } from "store/apis/group";
import { useGetCartQuery } from "store/apis/products";
import { initialStateForEntity, setCartInChat } from "store/slices/cartInChat";
import { BillingInvoice } from "types/Models/billing";
import { Entity } from "types/Models/entity";

type CartAttachmentViewInChatProps = {
  entityId: string;
  groupId: string;
  custom_data?: MessageComponentProps["message"]["custom_data"];
  card_status?: MessageComponentProps["message"]["card_status"];
  children?: ReactNode;
};

export const CartAttachmentPreviews = ({
  entityId,
  groupId,
}: CartAttachmentViewInChatProps) => {
  const { data: entities } = useGetGroupDataQuery(
    { groupId },
    {
      skip: !groupId,
    }
  );

  const entity = entities?.entities?.find(
    (entity: Entity) => entity.uuid === entityId
  );

  const dispatch = useAppDispatch();

  const clearCartAttachmentPreview = () => {
    dispatch(setCartInChat(initialStateForEntity));
  };

  const { data: cart } = useGetCartQuery(
    { entityId: entityId!, groupId },
    { skip: !entityId || !groupId, refetchOnMountOrArgChange: true }
  );

  const products_name =
    cart?.cart_items
      ?.map((item) => item.product_details?.product_name)
      ?.join(", ") || "";

  return (
    <>
      <CartAttachmentPreview
        entity={entity!}
        cartStatus="Cart Payment Pending"
        bodyStatus="items pending in your cart"
        headerIconClear={
          <button
            onClick={clearCartAttachmentPreview}
            className="all:unset t-rounded-full t-border t-border-neutral-20 t-border-solid t-p-1 t-h-6 t-w-6 t-flex t-items-center t-justify-center t-bg-neutral-10 t-cursor-pointer t-text-text-60"
          >
            <Cross color="currentColor" />
          </button>
        }
        Icon={<CartPaymentIcon />}
        color="yellow"
        groupId={groupId}
        cart_item_count={cart?.cart_items?.length}
        cart_item_name={products_name}
      />
    </>
  );
};

export const CartChatCard = ({
  entityId,
  card_status,
  custom_data,
  groupId,
  children,
}: CartAttachmentViewInChatProps) => {
  const isPaid = card_status !== "CART_UNPAID";
  const { isAdmin, isCustomer } = useRoleBasedView();
  const { data: cart, isLoading } = useGetCartQuery(
    { entityId, groupId },
    {
      skip:
        !entityId || !groupId || custom_data?.cart_item_count! > 0 || isPaid,
      refetchOnMountOrArgChange: true,
    }
  );
  const { trackEvent } = useAnalytics();
  const [open, setOpen] = useState<boolean>(false);
  const location = useLocation();

  const unpaidLink = isAdmin
    ? `/admin/crm/${groupId!}/billing/cart?entity=${entityId}`
    : `/settings/billing/cart?entity=${entityId}&settings_back_to=${encodeURIComponent(
        location.pathname + location.search
      )}`;

  const products_name =
    custom_data?.cart_item_name?.map((item) => item)?.join(", ") ||
    cart?.cart_items
      ?.map((item) => item.product_details?.product_name)
      ?.join(", ") ||
    "";

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <CartAttachmentPreview
        entity={{ uuid: entityId, name: custom_data?.entity_name || "" }}
        cartStatus={isPaid ? "Cart Paid" : "Cart Payment Pending"}
        Icon={isPaid ? <ChatCartPaid /> : <CartPaymentIcon />}
        color={isPaid ? "green" : "yellow"}
        footer={
          isPaid ? (
            <Button
              onClick={() => setOpen(true)}
              customType="secondary"
              size="small"
              block
            >
              View
            </Button>
          ) : (
            <ConditionalLink
              to={unpaidLink}
              onClick={() =>
                trackEvent(OPEN_CHECKOUT, {
                  type: "attach_cart",
                })
              }
            >
              <Button customType="primary" size="small" block>
                Review & Pay
              </Button>
            </ConditionalLink>
          )
        }
        bodyStatus={
          isPaid
            ? custom_data?.cart_item_count! > 1
              ? "Items paid"
              : "Item Paid"
            : "items pending in your cart"
        }
        groupId={groupId}
        cart_item_count={
          custom_data?.cart_item_count || cart?.cart_items?.length
        }
        cart_item_name={products_name!}
        isLoading={isLoading}
      />
      <Slider.Root open={open} onOpenChange={setOpen}>
        <Slider.Content open>
          <BillingSlider
            openCheckoutModal={(invoice: BillingInvoice) => {}}
            entityIdFromParent={entityId}
            invoiceIdFromParent={custom_data?.invoice_id}
            groupId={groupId}
          />
        </Slider.Content>
      </Slider.Root>
      {children}
    </div>
  );
};

const CartAttachmentPreview = ({
  entity,
  cartStatus,
  Icon,
  color,
  footer,
  headerIconClear,
  bodyStatus,
  groupId,
  cart_item_count,
  cart_item_name,
  isLoading,
}: {
  entity: Pick<Entity, "uuid" | "name">;
  cartStatus?: string;
  Icon?: ReactNode | null;
  color?: string;
  footer?: ReactNode | null;
  headerIconClear?: ReactNode;
  bodyStatus?: string;
  groupId: string;
  cart_item_count?: number;
  cart_item_name?: string;
  isLoading?: boolean;
}) => {
  const { data: cart } = useGetCartQuery(
    { entityId: entity?.uuid!, groupId },
    {
      skip: !entity?.uuid || cart_item_name?.length! > 0,
      refetchOnMountOrArgChange: true,
    }
  );

  return (
    <section className="t-pb-2 t-max-w-max t-relative">
      <div className="t-absolute -t-top-2 -t-right-2">{headerIconClear}</div>
      <ChatCard.Root>
        <ChatCard.Header>
          <div className="t-flex t-items-center t-gap-1.5 t-w-full t-px-1 t-justify-between">
            <div className="t-flex t-items-center t-gap-1.5">
              {Icon}
              <div>{cartStatus}</div>
            </div>
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          {isLoading ? (
            <section className="t-flex t-flex-col t-gap-1.5">
              {Array(3)
                .fill(null)
                .map((_, index) => (
                  <Skeleton key={index} className="t-h-3" />
                ))}
            </section>
          ) : (
            <>
              <Body
                color={color!}
                name={entity?.name || "Entity Name"}
                cart={cart_item_count || cart?.cart_items?.length}
                products_name={cart_item_name!}
                items_status={bodyStatus!}
              />
              {footer}
            </>
          )}
        </ChatCard.Body>
      </ChatCard.Root>
    </section>
  );
};

const Body = ({
  color,
  name,
  cart,
  products_name,
  items_status,
}: {
  color: string;
  name: string;
  cart?: number;
  products_name: string;
  items_status: string;
}) => {
  return (
    <section className="t-flex t-gap-1.5 t-flex-col">
      <div className="t-flex t-gap-2 t-w-full t-items-center">
        <div
          className={classNames("t-h-2 t-w-2 t-rounded-full", {
            "t-bg-green-70": color === "green",
            "t-bg-yellow-70": color === "yellow",
          })}
        />
        <div className="t-text-body-sm t-font-medium t-flex">
          {cart}
          &nbsp;
          {items_status}
        </div>
      </div>
      <div className="t-text-text-30 t-text-body-sm t-px-4 t-font-medium">
        {name || "Entity Name"}
      </div>
      <div className="t-text-text-30 t-text-body-sm t-px-4 t-font-normal">
        {products_name?.slice(0, 30)}...
      </div>
    </section>
  );
};
